import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createCamera } from './components/camera.js';
import { createRenderer } from './components/renderer.js';
import {
    addStudioLights,
    applyPrintedMaterial,
    placeOnGround,
    createSubtleGrid,
    frameCameraOnObject,
} from './components/studio.js';
import {
    applyLayoutTargets,
    computeLayoutTargets,
    createLayoutTween,
    hasSeparableParts,
    measureAssembly,
    stepLayoutTween,
    addCoasterStack,
} from './partsLayout.js';

// Recolecta las mallas del objeto cargado para el panel de colores: cada
// pieza necesita una key estable (nombre + ocurrencia, ya que el mismo
// nombre puede repetirse) y su color de partida.
function collectParts(object) {
    const parts = [];
    const nameOccurrences = {};
    object.traverse((child) => {
        if (child.isMesh) {
            const name = child.name || `pieza_${parts.length + 1}`;
            const occurrence = nameOccurrences[name] || 0;
            nameOccurrences[name] = occurrence + 1;
            const key = `${name}::${occurrence}`;
            const colorHex = child.material?.color
                ? `#${child.material.color.getHexString()}`
                : null;
            parts.push({
                id: child.uuid,
                key,
                name,
                colorHex,
                mesh: child,
                // Ancla de las disposiciones: abrir y cerrar cuantas veces se
                // quiera vuelve siempre a la misma posición de partida.
                basePosition: child.position.clone(),
            });
        }
    });
    return parts;
}

/**
 * Ciclo de vida Three.js compartido entre los visores de página completa
 * (Public3dViewer.jsx y Mi3DViewer.jsx): escena/cámara/renderer/luces/grid,
 * carga del GLB con DRACO, recolección de piezas para el panel de colores,
 * encuadre automático de cámara, reset view, resize y cleanup.
 *
 * Lo que cambia entre páginas (endpoint, cabeceras de autenticación, qué
 * hacer si la carga falla) se recibe por parámetro; el hook nunca decide
 * autenticación ni reintenta regenerar el modelo por su cuenta — eso es
 * responsabilidad de `onLoadError`, propia de cada página.
 */
function useModel3DViewer({ glbUrl, requestHeaders, bottomLight = false, onLoadError } = {}) {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    if (!sceneRef.current) sceneRef.current = new THREE.Scene();
    if (!cameraRef.current) cameraRef.current = createCamera();
    if (!rendererRef.current) rendererRef.current = createRenderer();
    const controlsRef = useRef(null);
    const objectRef = useRef(null);
    const gridHelperRef = useRef(null);
    // Mallas del modelo cargado, para aplicar el color elegido sin recargar el GLB.
    const partsRef = useRef([]);
    const onLoadErrorRef = useRef(onLoadError);
    // Leído por su valor (no por identidad) al arrancar cada carga: si fuera
    // dependencia directa del efecto principal, pasar un objeto literal
    // nuevo en cada render de la página (p. ej. `{ Authorization: ... }`
    // reconstruido cada vez) reiniciaría toda la escena en cada re-render.
    const requestHeadersRef = useRef(requestHeaders);

    useEffect(() => {
        onLoadErrorRef.current = onLoadError;
    }, [onLoadError]);

    useEffect(() => {
        requestHeadersRef.current = requestHeaders;
    }, [requestHeaders]);

    const [isGridVisible, setIsGridVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [error, setError] = useState(null);
    const [isResetting, setIsResetting] = useState(false);
    const [meshCount, setMeshCount] = useState(0);
    const [modelParts, setModelParts] = useState([]);
    const [partsColors, setPartsColors] = useState({});
    // Disposición de las piezas: sólo tiene sentido en productos de varias
    // piezas (la caja de luz). `canOpen` se deriva de las mallas del GLB, no
    // del tipo de producto, para que el visor público no necesite saberlo.
    const [partsLayout, setPartsLayout] = useState('assembled');
    const [canOpen, setCanOpen] = useState(false);
    const layoutTweenRef = useRef(null);
    const assemblyRef = useRef({ width: 0, frontDrop: 0 });

    /**
     * Separación de la tapa al abrir, en milímetros.
     *
     * Fija a propósito: aquí no hay nada que revisar antes de imprimir, sólo
     * enseñar que la caja se abre, así que no hay control de separación como en
     * el editor. 25mm despega la tapa lo justo para ver el interior sin que la
     * pieza se salga del encuadre que ya hizo la cámara.
     */
    const OPEN_GAP_MM = 25;

    const applyLayout = useCallback((layout, { animated = true } = {}) => {
        const targets = computeLayoutTargets(partsRef.current, layout, OPEN_GAP_MM, {
            modelWidthUnits: assemblyRef.current.width,
            frontDrop: assemblyRef.current.frontDrop,
        });
        if (!targets.length) return;
        if (!animated) {
            layoutTweenRef.current = null;
            applyLayoutTargets(targets);
            return;
        }
        layoutTweenRef.current = createLayoutTween(targets);
    }, []);

    /** Abre o cierra la caja. No hace nada en un modelo de una sola pieza. */
    const toggleOpen = useCallback(() => {
        setPartsLayout((prev) => {
            const next = prev === 'open' ? 'assembled' : 'open';
            applyLayout(next);
            return next;
        });
    }, [applyLayout]);

    const changePartColor = useCallback((part, color) => {
        const target = partsRef.current.find((p) => p.key === part.key);
        if (target?.mesh?.material?.color) {
            target.mesh.material.color.set(color);
            target.mesh.material.needsUpdate = true;
        }
        setPartsColors((prev) => ({ ...prev, [part.key]: color }));
    }, []);

    const initializeGridHelper = useCallback((size = 100, divisions = 100) => {
        if (!gridHelperRef.current) {
            gridHelperRef.current = createSubtleGrid(size, divisions);
            gridHelperRef.current.visible = isGridVisible;
        }
        return gridHelperRef.current;
    }, [isGridVisible]);

    const toggleGrid = useCallback(() => {
        setIsGridVisible((prev) => {
            const next = !prev;
            if (gridHelperRef.current) gridHelperRef.current.visible = next;
            return next;
        });
    }, []);

    const resetView = useCallback(async () => {
        if (!objectRef.current || !cameraRef.current || !controlsRef.current) {
            console.warn('Elementos 3D no están listos para resetear');
            return;
        }
        setIsResetting(true);
        try {
            await frameCameraOnObject(cameraRef.current, controlsRef.current, objectRef.current, {
                duration: 1.5,
                ease: 'power2.inOut',
            });
        } catch (err) {
            console.error('Error al resetear vista:', err);
        } finally {
            setIsResetting(false);
        }
    }, []);

    const cleanup = useCallback(() => {
        if (containerRef.current && rendererRef.current?.domElement) {
            try {
                containerRef.current.removeChild(rendererRef.current.domElement);
            } catch (e) {
                console.warn('Error al limpiar DOM:', e);
            }
        }

        sceneRef.current?.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) child.material.forEach((m) => m.dispose());
                else child.material.dispose();
            }
        });

        rendererRef.current?.dispose();
    }, []);

    useEffect(() => {
        if (!glbUrl) {
            setError('URL del modelo no proporcionada');
            return;
        }

        let animationId;
        let isMounted = true;

        const initializeViewer = async () => {
            try {
                setLoading(true);
                setError(null);
                setLoadingProgress(0);

                const container = containerRef.current;
                const scene = sceneRef.current;
                const camera = cameraRef.current;
                const renderer = rendererRef.current;

                if (!container) {
                    throw new Error('Contenedor no encontrado');
                }

                renderer.setSize(window.innerWidth, window.innerHeight);
                // Canvas transparente: el fondo lo pone el contenedor, que
                // sigue el tema claro/oscuro del design system.
                renderer.setClearColor(0x000000, 0);
                container.appendChild(renderer.domElement);

                addStudioLights(scene, { bottomLight });

                const gridHelper = initializeGridHelper(100, 100);
                scene.add(gridHelper);

                const controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.05;
                controls.enableZoom = true;
                controls.enablePan = true;
                controlsRef.current = controls;

                const dracoLoader = new DRACOLoader();
                dracoLoader.setDecoderPath('/node_modules/three/examples/jsm/libs/draco/gltf/');

                const gltfLoader = new GLTFLoader();
                gltfLoader.setDRACOLoader(dracoLoader);
                if (requestHeadersRef.current) {
                    gltfLoader.setRequestHeader(requestHeadersRef.current);
                }

                const loadModel = () => gltfLoader.load(
                    glbUrl,
                    (gltf) => {
                        if (!isMounted) return;

                        const object = gltf.scene;
                        // Sin escala extra: el GLB del backend ya viene
                        // calibrado al grid del visor (1 unidad Three.js = 1cm).
                        applyPrintedMaterial(object);

                        scene.add(object);
                        objectRef.current = object;

                        // Centrar el modelo y apoyar su base sobre la malla (y = 0)
                        placeOnGround(object);

                        const collectedParts = collectParts(object);
                        setMeshCount(collectedParts.length);
                        partsRef.current = collectedParts;
                        // La pila de posavasos, si la hay. Después de recolectar
                        // (no son piezas propias) y antes de medir.
                        addCoasterStack(object, collectedParts);
                        // Sin la malla ni la posición: lo que sale del hook a
                        // React tiene que ser serializable.
                        setModelParts(collectedParts.map(({ id, key, name, colorHex }) => (
                            { id, key, name, colorHex }
                        )));

                        // Medidas del conjunto ensamblado, antes de mover nada.
                        const assembly = measureAssembly(object, collectedParts);
                        assemblyRef.current = {
                            width: assembly.size.x || 0,
                            frontDrop: assembly.frontDrop,
                        };
                        setCanOpen(hasSeparableParts(collectedParts));
                        // Un modelo recién cargado se muestra siempre cerrado:
                        // es como se usa el producto.
                        setPartsLayout('assembled');
                        layoutTweenRef.current = null;
                        setPartsColors(Object.fromEntries(
                            collectedParts.map((part) => [part.key, part.colorHex || '#888888'])
                        ));

                        // Encuadra la cámara sobre el modelo (ver studio.js:
                        // frameCameraOnObject).
                        frameCameraOnObject(camera, controls, object);

                        setLoadingProgress(100);
                        setTimeout(() => setLoading(false), 500);
                    },
                    (progress) => {
                        if (progress.lengthComputable) {
                            setLoadingProgress((progress.loaded / progress.total) * 100);
                        }
                    },
                    (err) => {
                        if (!isMounted) return;
                        if (onLoadErrorRef.current) {
                            // Se pasan `setError`/`setLoading` en vez de dejar
                            // que la página cierre sobre el objeto que este
                            // hook devuelve, para no crear una referencia
                            // circular entre el callback y su propio hook.
                            onLoadErrorRef.current(err, { retry: loadModel, setError, setLoading });
                        } else {
                            console.error('Error cargando modelo:', err);
                            setError(`No se pudo cargar el modelo: ${err.message || 'Error desconocido'}`);
                            setLoading(false);
                        }
                    }
                );

                loadModel();

                const animate = () => {
                    if (!isMounted) return;
                    animationId = requestAnimationFrame(animate);
                    // La apertura se interpola desde el bucle que ya corre: ni
                    // un rAF extra ni una librería de animación.
                    if (!stepLayoutTween(layoutTweenRef.current)) {
                        layoutTweenRef.current = null;
                    }
                    controls.update();
                    renderer.render(scene, camera);
                };
                animate();

                const handleResize = () => {
                    if (!isMounted) return;
                    const width = window.innerWidth;
                    const height = window.innerHeight;
                    renderer.setSize(width, height);
                    camera.aspect = width / height;
                    camera.updateProjectionMatrix();
                };

                window.addEventListener('resize', handleResize);

                return () => {
                    window.removeEventListener('resize', handleResize);
                    if (animationId) cancelAnimationFrame(animationId);
                };
            } catch (err) {
                console.error('Error inicializando visor:', err);
                setError(`Error de inicialización: ${err.message}`);
                setLoading(false);
            }
        };

        initializeViewer();

        return () => {
            isMounted = false;
            if (animationId) cancelAnimationFrame(animationId);
            cleanup();
        };
        // `requestHeaders` se lee de `requestHeadersRef` a propósito, no
        // como dependencia (ver el comentario junto a su declaración).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [glbUrl, bottomLight, cleanup, initializeGridHelper]);

    return {
        containerRef,
        loading,
        loadingProgress,
        error,
        setError,
        setLoading,
        isResetting,
        meshCount,
        isGridVisible,
        toggleGrid,
        resetView,
        modelParts,
        partsColors,
        changePartColor,
        canOpen,
        isOpen: partsLayout === 'open',
        toggleOpen,
    };
}

export default useModel3DViewer;
